<template>
  <div>
    <v-row class="mb-4" align="center">
      <v-col>
        <h3 class="basic--text">
          <v-icon color="warning" class="mr-2">mdi-alert</v-icon>
          Some required eBay fields are missing.
          Fill them in below before uploading.
        </h3>
        <p class="grey--text mt-1">
          Fields marked with ✓ were auto-filled from product data.
          Only the gaps need your attention.
        </p>
      </v-col>
    </v-row>

    <!-- Per-product cards -->
    <v-card
      v-for="item in report"
      :key="item.sku"
      class="mb-4"
      outlined
    >
      <v-card-title class="pb-1">
        <v-icon class="mr-2" :color="item.missing.length === 0 ? 'success' : 'warning'">
          {{ item.missing.length === 0 ? 'mdi-check-circle' : 'mdi-alert-circle' }}
        </v-icon>
        {{ item.sku }}
        <v-spacer></v-spacer>
        <v-chip small color="grey lighten-2" class="mr-2">
          Category: {{ item.categoryId }}
        </v-chip>
      </v-card-title>

      <v-card-text>
        <!-- Auto-filled fields -->
        <div v-if="Object.keys(item.autoFilled).length > 0" class="mb-3">
          <p class="caption grey--text mb-1">AUTO-FILLED FROM PRODUCT DATA</p>
          <v-chip
            v-for="(val, key) in item.autoFilled"
            :key="key"
            small
            color="success"
            outlined
            class="mr-2 mb-2"
          >
            <v-icon small left>mdi-check</v-icon>
            {{ key }}: {{ val[0] }}
          </v-chip>
        </div>

        <!-- Missing fields to fill -->
        <div v-if="item.missing.length > 0">
          <p class="caption warning--text mb-2">REQUIRED — PLEASE FILL IN</p>
          <v-row>
            <v-col
              v-for="field in item.missing"
              :key="field.name"
              cols="12"
              sm="6"
              md="4"
            >
              <v-combobox
                v-model="userInputs[item.sku][field.name]"
                :items="field.validValues"
                :label="field.name"
                outlined
                dense
                clearable
                :rules="[v => !!v || `${field.name} is required`]"
              >
                <template v-slot:prepend-inner>
                  <v-icon small color="warning">mdi-asterisk</v-icon>
                </template>
              </v-combobox>
            </v-col>
          </v-row>
        </div>

        <div v-if="item.missing.length === 0" class="success--text">
          <v-icon color="success" small>mdi-check</v-icon>
          All required aspects are covered — ready to upload.
        </div>
      </v-card-text>
    </v-card>

    <!-- Actions -->
    <v-row justify="space-between" class="mt-4">
      <v-col cols="auto">
        <v-btn text @click="$emit('goBack')">
          <v-icon left>mdi-arrow-left</v-icon>
          Back
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <v-btn
          color="accent"
          large
          :disabled="!allFilled"
          :loading="saving"
          @click="confirm"
        >
          <v-icon left>mdi-upload</v-icon>
          Confirm & Upload
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: "FillAspects",
  props: {
    report: Array,   // from checkAspects API
    skus: Array,
    platform: Object,
  },
  data() {
    return {
      saving: false,
      // userInputs[sku][aspectName] = selectedValue
      userInputs: {},
    };
  },
  created() {
    // Initialize userInputs structure
    for (const item of this.report) {
      this.$set(this.userInputs, item.sku, {});
      for (const field of item.missing) {
        this.$set(this.userInputs[item.sku], field.name, null);
      }
    }
  },
  computed: {
    allFilled() {
      for (const item of this.report) {
        for (const field of item.missing) {
          if (!this.userInputs[item.sku]?.[field.name]) return false;
        }
      }
      return true;
    },
  },
  methods: {
    async confirm() {
      this.saving = true;
      try {
        // Build overrides map: { sku: { aspectName: [value] } }
        const aspectOverrides = {};
        for (const item of this.report) {
          aspectOverrides[item.sku] = {};

          // Include auto-filled
          for (const [key, val] of Object.entries(item.autoFilled)) {
            aspectOverrides[item.sku][key] = val;
          }

          // Include user inputs
          for (const [key, val] of Object.entries(this.userInputs[item.sku])) {
            if (val) aspectOverrides[item.sku][key] = [val];
          }
        }

        // Save to Firestore via backend
        await this.$store.dispatch("saveAspectOverrides", {
          overrides: aspectOverrides,
          platform: this.platform.platform,
        });

        this.$emit("confirmed", aspectOverrides);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>